import React, { useState, useEffect } from 'react'
import { Box, Stack, Typography, Button, useMediaQuery, useTheme } from '@mui/material'
import { DrawerPopup } from 'components/UI/DrawerPopup'
import { FieldLabel } from 'components/UI/FieldLabel'
import { FilterSheetFooter } from 'components/UI/FilterSheetFooter'
import { FILTER_PANEL_CONTENT_SX } from 'components/UI/filterPanelSx'
import { CertificatesDateRangeFilter, type DateRangeKey } from 'components/pages/certificates/CertificatesDateRangeFilter'
import { MobileDateRangeDrawer } from './MobileDateRangeDrawer'
import { ProvenanceOwnerSearchPicker, type ProvenanceOwner } from './ProvenanceOwnerSearchPicker'
import { createApiCall } from 'services'
import { useAutoApply } from 'hooks/useAutoApply'
import { border } from 'settings/tokens';

export interface ProvenanceFilterValues {
  ownerIds?: string[]
  dateFrom?: string
}

interface ProvenanceFilterProps {
  open: boolean
  onClose: () => void
  onApply: (values: ProvenanceFilterValues) => void
  certificateId: string
  initialOwnerIds?: string[]
  initialDateFrom?: string
}

export const ProvenanceFilter: React.FC<ProvenanceFilterProps> = ({ open, onClose, onApply, certificateId, initialOwnerIds = [], initialDateFrom = '' }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [ownerIds, setOwnerIds] = useState<string[]>(initialOwnerIds)
  const [dateFrom, setDateFrom] = useState<string>(initialDateFrom)
  const [dateOpen, setDateOpen] = useState<boolean>(false)
  const [ownerSearchOpen, setOwnerSearchOpen] = useState<boolean>(false)
  const [selectedRange, setSelectedRange] = useState<DateRangeKey>(null)
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDateFrom ? new Date(initialDateFrom) : null)
  const [owners, setOwners] = useState<ProvenanceOwner[]>([])

  const [tempOwnerIds, setTempOwnerIds] = useState<string[]>(ownerIds)

  const [tempSelectedRange, setTempSelectedRange] = useState<DateRangeKey>(selectedRange)
  const [tempSelectedDate, setTempSelectedDate] = useState<Date | null>(selectedDate)
  const [tempDateFrom, setTempDateFrom] = useState<string>(dateFrom)

  /*
   * AUTO-APPLY — no Apply button, at either breakpoint. Every control commits on a
   * 400ms debounce, the platform drawer's `AUTO_APPLY_MS`.
   *
   * Note what does NOT change: the two-tier `temp*` state below. Those are the
   * MOBILE DATE SUB-DRAWER's own staging, and that sub-drawer keeps its `Done` —
   * it is a picker with a single commit point of its own, not a filter surface
   * with an Apply. What auto-apply removes is the OUTER sheet's Apply; the inner
   * picker's Done is what promotes `temp*` into the values this commit reads.
   *
   * `open` is passed so a change made in the last 400ms before dismissal is
   * flushed — this drawer stays mounted between opens.
   */
  const { schedule } = useAutoApply(() => onApply({ ownerIds, dateFrom }), open)

  useEffect(() => {
    if (open && certificateId) {
      fetchOwners()
    }
  }, [open, certificateId])

  const fetchOwners = async () => {
    try {
      const { data, status } = await createApiCall({
        method: 'GET',
        url: `certificates/public/provenance/${certificateId}?limit=100`,
      })
      
      if (status === 200 && data?.data) {
        const uniqueOwners = new Map<string, ProvenanceOwner>()
        
        data.data.forEach((item: any) => {
          // Generate unique ID based on owner type
          let ownerId: string
          let ownerName: string
          let ownerEmail: string | undefined
          let ownerAvatar: string | undefined

          if (item.owner_type === 'customer') {
            // For customers, use email as unique identifier
            ownerId = item.owner.email || `customer-${Math.random()}`
            // VERA-304: empty, not 'Unknown'. A customer with no name is labelled by
            // their email at render time (see the rows below); fabricating a name here
            // would put a placeholder above the only real identifier the row has.
            ownerName = `${item.owner.first_name || ''} ${item.owner.last_name || ''}`.trim()
            ownerEmail = item.owner.email
            ownerAvatar = item.owner.avatar
          } else {
            // For companies, use company name as identifier
            ownerId = `company-${item.owner.name || item.certificate?.company?.name || 'Unknown'}`
            ownerName = item.owner.name || item.certificate?.company?.name || 'Unknown Company'
            ownerEmail = undefined
            ownerAvatar = item.owner.logo || item.certificate?.company?.logo
          }

          if (!uniqueOwners.has(ownerId)) {
            uniqueOwners.set(ownerId, {
              id: ownerId,
              name: ownerName,
              email: ownerEmail,
              avatar: ownerAvatar,
              owner_type: item.owner_type,
            })
          }
        })
        
        setOwners(Array.from(uniqueOwners.values()))
      }
    } catch (error) {
      console.error('Error fetching owners:', error)
    }
  }

  const selectedOwners = owners.filter(owner => ownerIds.includes(owner.id))

  const content = (
    /* 16px desktop gutter, matching ActivityFilterDrawer / ScansFilterDrawer and
       DrawerPopup's own hard-coded 16px header inset. This was `p: 1` — 8px, and on
       all four sides, so this panel's fields sat on a different left edge from its two
       sibling filter panels AND from its own title. `px` only: the vertical inset is
       DrawerPopup's `py: 3`, and paying it twice double-counted. */
    <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column', px: isMobile ? 0 : 2 }}>
      {/* DESKTOP: `flex: '1 1 auto'` + `minHeight: 0` + `overflowY: auto`. This body had
          `flex: 1` and NO scroll region, so once the owner list or the date section grew
          past the panel it pushed the footer off the bottom rather than scrolling under
          it. Its two siblings already scroll; this one did not.

          MOBILE keeps `flex: 1` and NO overflow: the bottom sheet is content-height and
          the PAPER is the scroll owner (`maxHeight: 90vh` + MUI's `overflowY: auto`,
          which is what DrawerPopup's sticky header exists for). A second scroll container
          inside it draws a scrollbar over content that had nothing to scroll — measured
          on the certificates sheet as a 297px box around 301px of content. */}
      <Stack
        spacing={3}
        sx={isMobile ? { flex: 1 } : { flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}
      >
        <Stack spacing={1}>
          <FieldLabel
            label="Owner"
          />
          <ProvenanceOwnerSearchPicker
            mobile={isMobile}
            open={ownerSearchOpen}
            onOpen={() => {
              setTempOwnerIds(ownerIds)
              setOwnerSearchOpen(true)
            }}
            onClose={() => {
              setTempOwnerIds(ownerIds)
              setOwnerSearchOpen(false)
            }}
            certificateId={certificateId}
            selectedOwnerIds={tempOwnerIds}
            onToggleOwnerId={(id, checked) => setTempOwnerIds(prev => checked ? [...prev, id] : prev.filter(x => x !== id))}
            onClear={() => setTempOwnerIds([])}
            onApply={() => {
              setOwnerIds(tempOwnerIds)
              setOwnerSearchOpen(false)
              schedule()
            }}
          />
          {ownerIds.length > 0 && (
            <Stack spacing={1}>
              <Typography sx={{ color: '#1f2937', fontSize: 14 }}>Selected items: {ownerIds.length}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedOwners.map(owner => (
                  <Box
                    key={owner.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      backgroundColor: theme => theme.palette.grey[50],
                      borderRadius: '12px',
                      px: 1.5,
                      py: 0.75,
                    }}
                  >
                    <Typography sx={{ fontSize: 14 }}>{owner.name || owner.email}</Typography>
                    <Box
                      // Same rule as the Clear link above: removing a chip edits the
                      // pending selection, it does not commit it.
                      onClick={() => {
                        const newOwnerIds = ownerIds.filter(id => id !== owner.id)
                        setOwnerIds(newOwnerIds)
                        setTempOwnerIds(newOwnerIds)
                        schedule()
                      }}
                      sx={{
                        cursor: 'pointer',
                        fontSize: 16,
                        fontWeight: 'bold',
                      }}
                    >
                      ×
                    </Box>
                  </Box>
                ))}
              </Box>
            </Stack>
          )}
        </Stack>

        <Stack spacing={1}>
          {/*
            A SECTION heading, like Owner above it and like every section in the
            Activity and Scans filter sheets — not the date control's own
            `fieldLabel`.

            Those are two different roles that happened to render as siblings
            here, and once section headings became uppercase captions the
            difference was visible: "OWNER" in caps sitting above "Ownership
            date" in sentence case, for two controls that are plainly parallel.
            Activity and Scans had already resolved this the same way (heading
            stays, control's own label suppressed with fieldLabel='') — this was
            the one sheet still doing the opposite.
          */}
          <FieldLabel label="Ownership date" />
          <CertificatesDateRangeFilter
            mobile={isMobile}
            selectedRange={selectedRange}
            selectedDate={selectedDate}
            open={dateOpen}
            onOpen={() => {
              setTempSelectedRange(selectedRange)
              setTempSelectedDate(selectedDate)
              setTempDateFrom(dateFrom)
              setDateOpen(true)
            }}
            onClose={() => {
              setTempSelectedRange(selectedRange)
              setTempSelectedDate(selectedDate)
              setTempDateFrom(dateFrom)
              setDateOpen(false)
            }}
            /* Complete states, all three — a single date or a named preset, never a
               half-drawn range. They commit at once. */
            onClear={() => { setSelectedRange(null); setSelectedDate(null); setDateFrom(''); schedule() }}
            onSelectRange={(key) => { setSelectedRange(key); setSelectedDate(null); setDateFrom(''); schedule() }}
            onSelectDate={(d) => { setSelectedRange(null); setSelectedDate(d); setDateFrom(d ? new Date(d).toISOString().slice(0,10) : ''); schedule() }}
            fieldLabel=""
          />

          {isMobile && (
            <MobileDateRangeDrawer
              open={dateOpen}
              title="Ownership date"
              selectedRange={tempSelectedRange}
              selectedDate={tempSelectedDate}
              onSelectRange={(key) => { setTempSelectedRange(key); setTempSelectedDate(null); setTempDateFrom('') }}
              onSelectDate={(d) => { setTempSelectedRange(null); setTempSelectedDate(d); setTempDateFrom(d ? new Date(d).toISOString().slice(0,10) : '') }}
              onClear={() => { setTempSelectedRange(null); setTempSelectedDate(null); setTempDateFrom('') }}
              /* The sub-drawer's own commit point — it promotes `temp*` into the
                 values this sheet holds, which is the edit auto-apply then commits. */
              onDone={() => {
                setSelectedRange(tempSelectedRange)
                setSelectedDate(tempSelectedDate)
                setDateFrom(tempDateFrom)
                setDateOpen(false)
                schedule()
              }}
              onClose={() => {
                setTempSelectedRange(selectedRange)
                setTempSelectedDate(selectedDate)
                setTempDateFrom(dateFrom)
                setDateOpen(false)
              }}
            />
          )}
        </Stack>
      </Stack>

      <FilterSheetFooter
        /* `panel` on desktop: right-aligned, intrinsic-width, 40px — the same footer
           geometry the PLATFORM's filter drawer renders from this same component. A
           50/50 pair of 44px thumb targets is a phone-sheet shape and read as one on a
           400px pointer-driven side panel. `sheet` on mobile is unchanged. */
        mode={isMobile ? 'sheet' : 'panel'}
        /* RESET ONLY. Omitting `onApply` selects the shared component's auto-apply
           mode; `count` went with the button it labelled. Reset commits (clearing IS
           the change) and still does not close the sheet. */
        onReset={() => {
          setOwnerIds([])
          setTempOwnerIds([])
          setSelectedRange(null)
          setSelectedDate(null)
          setDateFrom('')
          setTempSelectedRange(null)
          setTempSelectedDate(null)
          setTempDateFrom('')
          schedule()
        }}
      />
    </Stack>
  )

  return (
    <DrawerPopup
      title="Filters"
      /* NO 'Cancel' — the header X, which is what DrawerPopup draws when no right
         action is given (and what this drawer's desktop branch already showed).
         Under auto-apply nothing is pending, so "Cancel" would promise a revert
         that cannot happen; Reset clears to empty, which is not the same thing. */
      anchor={isMobile ? 'bottom' : 'right'}
      child={content}
      handleOpen={open}
      handleClose={onClose}
      /* Desktop only — makes the content box FILL the panel so the footer's
         `marginTop: auto` has space to push Reset/Apply to the bottom. See
         `FILTER_PANEL_CONTENT_SX` for why `height: 100%` alone did not. */
      contentSx={isMobile ? undefined : FILTER_PANEL_CONTENT_SX}
      // Content-height, like every other filter sheet. This was the last
      // `100vh` bottom sheet in the app — a two-field filter that opened as a
      // full-page takeover (VERA-294's rule, and the certificates template).
      paperSx={
        isMobile
          ? { height: 'auto', maxHeight: '90vh' }
          : { width: 400, maxWidth: '80vw', mr: 2, my: 2, borderRadius: '12px', height: 'calc(100% - 32px)' }
      }
    />
  )
}

export default ProvenanceFilter


